const { Type } = require('@sinclair/typebox');
const Blockly = require('blockly');
const dataAccessMethods = require('../../../../dataAccessMethods');
const setupAddCodeProperties = require('../../../../helpers/addCodeProperties');
const arrayFromCommaSeparatedValues = require('../../../../helpers/arrayFromCommaSeparatedValues');
const distinct = require('lodash/uniq');
const TOOLTIP = 'Criteria element for use on FORMS to document a constrain on multiple values of a dropdown field';

/**
 * @param {*} color
 * @param {import('blockly').Block} blocklyBlock reference to the block as a whole
 * @param {Object} mdcBlock Meta Data Configuration block. the group definition from blockGroupConfiguration.js
 * @param {import('blockly').Input} line1OfInputs First line of inputs, already including the Block name and the title field
 */
function addBlockSpecificFields(color, blocklyBlock, mdcBlock, line1OfInputs) {
    let workspace = blocklyBlock.workspace;
    let blockId = blocklyBlock.id;

    blocklyBlock.setTooltip(TOOLTIP);
    //set inputs to be arranged vertically.
    //Meaning each (dummy)input starts a new line
    blocklyBlock.setInputsInline(false);

    line1OfInputs
        .appendField('  Namespace:')
        .appendField(new Blockly.FieldDropdown(dataAccessMethods.getNamespaces), 'namespace')
        .appendField('  Relation:')
        .appendField(new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId)), 'relation');

    //line 2 of inputs
    blocklyBlock
        .appendDummyInput()
        .setAlign(Blockly.ALIGN_RIGHT)
        .appendField('  Properties to include in dropdown:')
        .appendField(new Blockly.FieldTextInput('title'), 'dropdownDisplayProperties')
        .appendField('  Properties to include in input:')
        .appendField(new Blockly.FieldTextInput('title'), 'inputDisplayProperties')
        .appendField('  Tooltip:')
        .appendField(new Blockly.FieldTextInput(''), 'tooltip');

    blocklyBlock.appendStatementInput('filters').setCheck(['filter']).appendField('Filters:');
}

/**
 * @param {{[key: string]: any}} code
 * @param {import('blockly').Block} blocklyBlock
 * @return {{[key: string]: any}}
 */
function convertCustomFieldsToCode(code, blocklyBlock) {
    const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);

    addCodeProperties([
        { key: 'foreignNamespace', value: 'namespace' },
        { key: 'foreignRelation', value: 'relation' },
        { key: 'dropdownDisplayProperties', handler: arrayFromCommaSeparatedValues },
        { key: 'inputDisplayProperties', handler: arrayFromCommaSeparatedValues },
        'tooltip'
    ]);

    //nested blocks, including those in `filters` are converted and added automatically

    return code;
}

/** Builds the criteria schema fragment for the MultiPickerDropDownCriteria workflow element. */
function asDataDictionary(hNode, context) {
    const {
        id,
        title,
        tooltip,
        foreignNamespace,
        foreignRelation,
        dropdownDisplayProperties = [],
        inputDisplayProperties = [],
        required = false
    } = hNode;

    const nestedProperties = distinct([...dropdownDisplayProperties, ...inputDisplayProperties]).filter(Boolean);
    
    // Build TypeBox schemas for nested string properties
    const nestedTypeDef = nestedProperties.reduce(
        (soFar, propertyName) => {
            return {
                ...soFar,
                [propertyName]: Type.String({
                    title: propertyName,
                    'x-dataType': 'string',
                    'x-propertyName': propertyName
                })
            };
        },
        {
            _id: Type.String({
                title: 'Unique ID',
                'x-dataType': 'string',
                'x-propertyName': '_id'
            })
        }
    );

    // we could be on an update form, or on a new form, or nested somewhere
    // We just don't know what accessContext this would be enabling, nor how it is applied on a NS/REL
    // Security Modifiers (which would disabled certain accessContext) are processed generically
    // All we know is that Whatever propertyName is on this Criteria block
    // and how it is formatted.
    
    const requiredProps = ['_id', ...nestedProperties];
    const itemSchema = Type.Object(nestedTypeDef, {
        title: 'Item',
        'x-dataType': 'object',
        additionalProperties: false,
        required: requiredProps
    });

    const inSchema = Type.Optional(
        Type.Array(itemSchema, {
            title: 'Test for inclusion',
            'x-dataType': 'array',
            'x-propertyName': '_in',
            'x-optional': !required
        })
    );

    const ninSchema = Type.Optional(
        Type.Array(itemSchema, {
            title: 'Test for exclusion',
            'x-dataType': 'array',
            'x-propertyName': '_nin',
            'x-optional': !required
        })
    );

    // Create main criteria object
    const criteriaSchema = Type.Object(
        {
            _in: inSchema,
            _nin: ninSchema
        },
        {
            'x-id': id,
            title: title,
            'x-tooltip': tooltip,
            'x-propertyName': `${foreignNamespace}:${foreignRelation}`,
            'x-dataType': 'object',
            'x-optional': !required,
            additionalProperties: false
        }
    );

    const partialTypeDefinition = {
        [`${foreignNamespace}:${foreignRelation}`]: required ? criteriaSchema : Type.Optional(criteriaSchema)
    };

    return [{}, partialTypeDefinition];
}

function customizeToolbox(block) {
    block.inputs = {
        filters: {
            block: {
                type: 'filter_OrderBy',
                fields: { propertyName: 'title' }
            }
        }
    };
}

module.exports = { addBlockSpecificFields, convertCustomFieldsToCode, asDataDictionary, customizeToolbox };
